import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import type { OpenQuestion, OpenQuestionsList } from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import {
  bridgeEventStreamAtom,
  daemonPushFrameStreamAtom,
  sdkConfiguredAtom,
} from '../stores/sdk/atoms';

export interface PositionOpenQuestions {
  questions: OpenQuestion[] | null;
  error: Error | null;
}

function asError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err));
}

export function usePositionOpenQuestions(positionId: string): PositionOpenQuestions {
  const ready = useAtomValue(sdkConfiguredAtom);
  const authenticated = useAtomValue(holdsIdentityAtom);
  const webStream = useAtomValue(bridgeEventStreamAtom);
  const daemonStream = useAtomValue(daemonPushFrameStreamAtom);
  const [list, setList] = useState<OpenQuestionsList | null>(null);
  const [questions, setQuestions] = useState<OpenQuestion[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setList(null);
    setQuestions(null);
    setError(null);
    if (!ready || !authenticated) return;
    let cancelled = false;
    let offChange: (() => void) | undefined;
    getSDK()
      .questions.connectForPosition(positionId)
      .then((opened) => {
        if (cancelled) return;
        offChange = opened.onChange((items) => {
          setQuestions(items);
          setError(null);
        });
        setQuestions(opened.items);
        setList(opened);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(asError(err));
      });
    return () => {
      cancelled = true;
      offChange?.();
    };
  }, [positionId, ready, authenticated]);

  useEffect(() => {
    if (!list) return;
    const stream = webStream ?? daemonStream;
    if (!stream) return;
    return list.follow(stream, (err: unknown) => {
      setError(asError(err));
    });
  }, [list, webStream, daemonStream]);

  return { questions, error };
}
